import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SearchBar from "@/components/SearchBar";
import {
  LayoutDashboard, Boxes, Wrench, Users as UsersIcon, FileText, Settings,
  LogOut, Menu, X, Car, ShoppingCart, Library, Recycle, Banknote,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, full: false, end: true },
  { to: "/stock", label: "Stock & Pneus", icon: Boxes, full: false },
  { to: "/vhu", label: "Pièces VHU", icon: Recycle, full: false },
  { to: "/reparations", label: "Réparations", icon: Wrench, full: false },
  { to: "/encaissement", label: "Encaissement", icon: Banknote, full: true },
  { to: "/clients", label: "Clients & Véhicules", icon: UsersIcon, full: true },
  { to: "/commandes", label: "À commander", icon: ShoppingCart, full: true },
  { to: "/catalogue", label: "Catalogue pièces", icon: Library, full: true },
  { to: "/devis", label: "Devis", icon: FileText, full: true },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Settings, full: true },
];

export default function Layout() {
  const { user, isFull, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => !n.full || isFull);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleLabel =
    user.role === "reparateur" ? "Mécanicien" : "Secrétariat / Direction";
  const roleBadge =
    user.role === "reparateur"
      ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
      : "bg-purple-500/10 text-purple-400 border-purple-500/30";

  return (
    <div className="min-h-screen bg-[#0A0D14] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-64 bg-[#121824] border-r border-[#2A364F] flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 flex items-center gap-3 border-b border-[#2A364F]">
          <div className="w-10 h-10 rounded-lg bg-[#F59E0B] flex items-center justify-center">
            <Car className="w-6 h-6 text-[#0A0D14]" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-[#F8FAFC] leading-tight">MA SERVICES</div>
            <div className="text-[10px] text-[#64748B] tracking-widest uppercase">Gestion Atelier</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#3B82F6] text-white"
                    : "text-[#94A3B8] hover:bg-[#1E2A3E] hover:text-[#F8FAFC]"
                }`
              }
            >
              <n.icon className="w-5 h-5 shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#2A364F]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-[#F8FAFC] truncate">{user.name}</div>
            <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded border ${roleBadge}`}>
              {roleLabel}
            </span>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
          >
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-[#121824] border-b border-[#2A364F] px-4 py-3 flex items-center gap-3">
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-[#F8FAFC] lg:hidden shrink-0">
            {open ? <X /> : <Menu />}
          </button>
          <span className="font-heading font-bold text-[#F8FAFC] lg:hidden shrink-0">MA SERVICES</span>
          <div className="flex-1 max-w-2xl">
            <SearchBar />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
